<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog as="div" class="relative z-50" @close="close">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="close" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-2xl">
                <div class="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                  <!-- Header -->
                  <div class="px-6 py-5 bg-indigo-50 border-b border-gray-200">
                    <div class="flex items-start justify-between">
                      <div class="flex-1 min-w-0">
                        <DialogTitle class="text-xl font-bold text-gray-900">
                          {{ task?.taskName || task?.task_name || 'Task Details' }}
                        </DialogTitle>
                      </div>
                      <button
                        type="button"
                        class="ml-4 rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        @click="close"
                      >
                        <span class="sr-only">Close</span>
                        <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="flex-1 px-6 py-6 bg-gray-50">
                    <div v-if="task" class="space-y-6">
                      <!-- Description Card -->
                      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                        <div class="flex items-center mb-3">
                          <DocumentTextIcon class="h-5 w-5 text-gray-500 mr-2" />
                          <h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                            Description
                          </h3>
                        </div>
                        <p
                          class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed break-words"
                          :class="{
                            'text-gray-400 italic': !task.description,
                          }"
                        >
                          {{ task.description || 'No description provided.' }}
                        </p>
                      </div>

                      <!-- Task Details Grid -->
                      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                        <div class="flex items-center mb-4">
                          <InformationCircleIcon class="h-5 w-5 text-gray-500 mr-2" />
                          <h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                            Task Information
                          </h3>
                        </div>
                        <div class="grid grid-cols-1 gap-5">
                          <!-- Status -->
                          <div class="flex items-start">
                            <div class="flex-shrink-0">
                              <div
                                :class="[
                                  task.status === 'completed'
                                    ? 'bg-green-100 text-green-600'
                                    : 'bg-yellow-100 text-yellow-600',
                                  'rounded-lg p-2',
                                ]"
                              >
                                <CheckCircleIcon
                                  v-if="task.status === 'completed'"
                                  class="h-5 w-5"
                                />
                                <ClockIcon v-else class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Status
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{ task.status === 'completed' ? 'Completed' : 'Not Completed' }}
                              </p>
                            </div>
                          </div>

                          <!-- Assignee -->
                          <div class="flex items-start">
                            <div class="flex-shrink-0">
                              <div class="bg-blue-100 text-blue-600 rounded-lg p-2">
                                <UserIcon class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Assignee
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{
                                  task.assignee?.full_name ||
                                  task.assignee?.fullName ||
                                  task.assignee?.email ||
                                  '—'
                                }}
                              </p>
                            </div>
                          </div>

                          <!-- Assigned By -->
                          <div class="flex items-start">
                            <div class="flex-shrink-0">
                              <div class="bg-purple-100 text-purple-600 rounded-lg p-2">
                                <UserPlusIcon class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Assigned By
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{
                                  task.assigner?.full_name ||
                                  task.assigner?.fullName ||
                                  task.assigner?.email ||
                                  '—'
                                }}
                              </p>
                            </div>
                          </div>

                          <!-- Date Assigned -->
                          <div class="flex items-start">
                            <div class="flex-shrink-0">
                              <div class="bg-indigo-100 text-indigo-600 rounded-lg p-2">
                                <CalendarIcon class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Date Assigned
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{ formatDate(task.dateTaskAssigned || task.date_task_assigned) }}
                              </p>
                            </div>
                          </div>

                          <!-- Due Date -->
                          <div v-if="task.dueDate || task.due_date" class="flex items-start">
                            <div class="flex-shrink-0">
                              <div class="bg-orange-100 text-orange-600 rounded-lg p-2">
                                <CalendarIcon class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Due Date
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{ formatDate(task.dueDate || task.due_date) }}
                              </p>
                            </div>
                          </div>

                          <!-- Date Completed -->
                          <div
                            v-if="
                              task.status === 'completed' &&
                              (task.dateTaskCompleted || task.date_task_completed)
                            "
                            class="flex items-start"
                          >
                            <div class="flex-shrink-0">
                              <div class="bg-green-100 text-green-600 rounded-lg p-2">
                                <CheckBadgeIcon class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Date Completed
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{ formatDate(task.dateTaskCompleted || task.date_task_completed) }}
                              </p>
                            </div>
                          </div>

                          <!-- Canvassing Organization -->
                          <div class="flex items-start">
                            <div class="flex-shrink-0">
                              <div class="bg-gray-100 text-gray-600 rounded-lg p-2">
                                <BuildingOfficeIcon class="h-5 w-5" />
                              </div>
                            </div>
                            <div class="ml-3 flex-1">
                              <p
                                class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1"
                              >
                                Canvassing Organization
                              </p>
                              <p class="text-sm font-semibold text-gray-900">
                                {{ task.canvassingOrg?.name || task.canvassing_org?.name || '—' }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Actions -->
                      <div
                        class="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-3"
                      >
                        <!-- Mark Complete / Reopen -->
                        <button
                          v-if="task.status !== 'completed' && canMarkComplete(task)"
                          @click="markComplete"
                          class="w-full flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <CheckCircleIcon class="h-5 w-5 mr-2" />
                          Mark as Completed
                        </button>
                        <button
                          v-else-if="task.status === 'completed' && canReopen(task)"
                          @click="reopen"
                          class="w-full flex items-center justify-center px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg shadow-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <ClockIcon class="h-5 w-5 mr-2" />
                          Reopen Task
                        </button>
                        <div
                          v-else-if="task.status === 'completed'"
                          class="w-full flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 font-semibold rounded-lg border border-green-200"
                        >
                          <CheckCircleIcon class="h-5 w-5 mr-2" />
                          Task Completed
                        </div>

                        <!-- Edit and Delete (for assigner/admins) -->
                        <div v-if="canEdit(task) || canDelete(task)" class="flex gap-3">
                          <button
                            v-if="canEdit(task)"
                            @click="edit"
                            class="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <PencilIcon class="h-5 w-5 mr-2" />
                            Edit
                          </button>
                          <button
                            v-if="canDelete(task)"
                            @click="deleteTask"
                            class="flex-1 flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <TrashIcon class="h-5 w-5 mr-2" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  UserIcon,
  UserPlusIcon,
  CalendarIcon,
  CheckBadgeIcon,
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  task: {
    type: Object,
    default: null,
  },
  canMarkComplete: {
    type: Function,
    default: () => false,
  },
  canReopen: {
    type: Function,
    default: () => false,
  },
  canEdit: {
    type: Function,
    default: () => false,
  },
  canDelete: {
    type: Function,
    default: () => false,
  },
})

const emit = defineEmits(['close', 'markComplete', 'reopen', 'edit', 'delete'])

const close = () => {
  emit('close')
}

const markComplete = () => {
  emit('markComplete', props.task)
}

const reopen = () => {
  emit('reopen', props.task)
}

const edit = () => {
  emit('edit', props.task)
}

const deleteTask = () => {
  emit('delete', props.task)
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return '—'
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch (error) {
    console.error('Error formatting date:', error, dateString)
    return '—'
  }
}
</script>
